import { SimCardDownload } from '@mui/icons-material';
import { Grid, Button, Paper, Box, CircularProgress } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { downloadInvoicePdf } from 'src/shared/functions/downloadInvoicePdf';
import { getInvoiceById } from 'src/shared/services/invoiceService';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { Invoice } from '../models/invoice';
import SendInvoiceMenu from '../components/sendInvoiceMenu';
import { InvoiceStatuses } from '../components/invoiceStatuses';
import InvoiceMissionDetails from '../components/invoiceMissionDetails';
import InvoiceEventsDetails from '../components/invoiceEventsDetails';
import { InvoiceDeliveryChip } from '../components/invoiceDeliveryChip';
import HandleInvoiceMenu from '../components/handleInvoiceMenu';
import { InvoiceDetailSummary } from '../components/invoiceDetailSummary';
// import ExpandableInvoiceText from '../components/expandableInvoiceText';

export const InvoiceDetailsPage = () => {
  const { t } = useTranslation(['invoice', 'common', 'dashboard']);
  const [loading, setIsLoading] = useState(true);
  const [invoice, setInvoice] = useState<Invoice>();
  const isMounted = useIsMounted();
  const params = useParams();

  const fetchData = useCallback(async () => {
    try {
      const result = await getInvoiceById(params.id);
      if (isMounted()) {
        ConsoleHelper.log('INVOICE DATA', result.data);
        setInvoice(result.data);
      }
    } catch (err) {
      if (isMounted()) ConsoleHelper.log(err);
    } finally {
      if (isMounted()) setIsLoading(false);
    }
  }, [isMounted, params.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const pdfLoadingCallback = useCallback((isLoading: boolean) => {
    setIsLoading(isLoading);
  }, []);

  const downloadPdf = async (id) => {
    downloadInvoicePdf(id, pdfLoadingCallback);
  };

  return (
    <GridWrapper>
      <Grid item xs={12} lg={11} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <MainContentHeader title={`${t('dashboard:invoices.chartLabels.invoice')} #${invoice?.ocr}`} isLoading={loading} />
          {invoice?.status && (
            <Box ml={1} mr={1}>
              <InvoiceStatuses status={invoice?.status} chipSize='small' />
            </Box>
          )}
          <InvoiceDeliveryChip sendings={invoice?.sendings ?? []} chipSize='small' />
        </Box>
        {invoice && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button variant='outlined' onClick={() => downloadPdf(invoice?.invoice_id)} endIcon={<SimCardDownload />} sx={{ whiteSpace: 'nowrap' }}>
              {t('invoice:DownloadAsPDF')}
            </Button>
            <HandleInvoiceMenu disabled={false} invoice={invoice} refetchData={fetchData} />
            <SendInvoiceMenu
              invoiceId={invoice.invoice_id}
              refetchData={fetchData}
              hasEInvoiceAddress={!!invoice?.member?.eInvoiceAddress?.eInvoiceReceiver_id}
              recipientName={invoice?.member?.eInvoiceAddress?.receiver?.name}
            />
          </Box>
        )}
      </Grid>
      <Grid item xs={12} lg={11}>
        <Paper sx={{ p: 3, overflow: 'hidden' }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress role='alert' aria-busy='true' aria-label={t('common:statusMessages.loading')} />
            </Box>
          )}
          {!loading && invoice && (
            <GridWrapper>
              <Grid item xs={12} xl={8} pr='24px'>
                <GridWrapper>
                  <Grid item xs={12}>
                    <InvoiceDetailSummary invoice={invoice} />
                  </Grid>
                </GridWrapper>
              </Grid>
              <Grid item xs={12} xl={4} sx={(theme) => ({ borderLeft: { xs: 'none', xl: `1px solid ${theme.palette.divider}` } })}>
                <GridWrapper>
                  <Grid item xs={12}>
                    <InvoiceMissionDetails invoice={invoice} balance={invoice?.amount - invoice?.paid} refetchData={fetchData} />
                  </Grid>
                  <Grid item xs={12}>
                    <InvoiceEventsDetails events={invoice?.events ?? []} />
                  </Grid>
                </GridWrapper>
              </Grid>
            </GridWrapper>
          )}
        </Paper>
      </Grid>
    </GridWrapper>
  );
};
